/* eslint max-lines-per-function: off */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import JSONBig from 'json-bigint';
import { useTranslation } from 'react-i18next';
import { DeclarationLocationState, OuDeclarationTarget, UseDataProps } from './types';
import useHydrateChoiceList from './hooks/useHydrateDeclaration/useHydrateChoiceList';
import { DECLARATIONS, FORMS, INBOXES } from '10.quickConnect.app/routes/paths';
import useEffectOnce from '80.quickConnect.Core/hooks/useEffectOnce';
import useGetDeclarationContext from '10.quickConnect.app/components/domain/Declaration/hooks/useGetFormContext';
import useHydrateDeclarationWithEquipment from '10.quickConnect.app/components/domain/Declaration/hooks/useHydrateDeclaration/useHydrateDeclarationWithEquipment';
import useHydrateDeclarationWithInternalData from '10.quickConnect.app/components/domain/Declaration/hooks/useHydrateDeclaration/useHydrateDeclarationWithInternalData';
import CustomLogger from '80.quickConnect.Core/logger/customLogger';
import { useStore } from '30.quickConnect.Stores';
import {
  FieldDesc,
  ItemData,
  WorkflowData,
  DeclarationContext,
  EntityData,
  RepeatableGroupDesc,
} from '90.quickConnect.Models/models';
import {
  errorHandler,
  flatten,
  getDeclarationEditLockName,
  getInboxEditLockName,
  getFormEditLockName,
} from '80.quickConnect.Core/helpers';
import { mapFieldDesc } from '90.quickConnect.Models/mappings';
import { FieldType, FormType, WorkflowStatus } from '90.quickConnect.Models/enums';
import { OuDecision } from '90.quickConnect.Models/models/user/ouDecision';
import {
  setHistoricValues,
  getSharedListIds,
} from '10.quickConnect.app/components/domain/Declaration/helpers/initForm';
import {
  getEntityDataByDeepLink,
  setDeepLinkValues,
} from '10.quickConnect.app/components/domain/Declaration/helpers/initForm/deepLink';
import { useFieldAutoComplete } from '50.quickConnect.Fields/hooks/useFieldAutoComplete';
import { mapDesactivatedAutomation } from '90.quickConnect.Models/mappings';
import { desactivatedAutomationWkf } from '10.quickConnect.app/components/domain/Declaration/helpers/initForm/desactivatedAutomationWkf';
import { useHydrateDefaultChoices } from '10.quickConnect.app/components/domain/Declaration/hooks/useHydrateDeclaration/useHydrateDefaultChoices';
import { updatePropertyFieldOrChild } from '20.formLib/helpers/updateField/updatePropertyFieldOrChild';
import useOuSwitchPrompt from '10.quickConnect.app/components/shared/Dialogs/OuSwitchPrompt/useOuSwitchPrompt';
import { getOuPromptContext } from '10.quickConnect.app/components/domain/Declaration/helpers/getOuPromptContext';
import useEditLock from '10.quickConnect.app/components/domain/Declaration/hooks/useEditLock';
import useOpenedElsewherePrompt from '10.quickConnect.app/components/shared/Dialogs/OpenedElsewherePrompt/useOpenedElsewherePrompt';

const useData = (isDuplicated: boolean | undefined, isDeepLink: boolean | undefined): UseDataProps => {
  // On récupère le store
  const {
    DeclarationStore: {
      openDialog,
      openDeclarationDialog,
      mustConfirmDeclarationExit,
      openEntry,
      closeEntry,
      isDeletedDeclarationContext,
      setIsDeletedDeclarationContext,
      setCloseDrawer,
      setDeclarationStoreId,
      setIsDeepLink,
      setUrlToUseForImagesBank,
      currentBaseScope,
      getDeclarationOuCode,
      isFormEditedElsewhereAsync,
    },
    FormStore: { getFormsList },
    InboxesStore: { getInboxOuCode, getInboxSubject },
    OuAccessStore: { decisionForForm, decisionForDeclaration, decisionForInbox },
    EquipmentsStore: { getEntityData, resetIsInDataSource, synchronizeEquipementsMultipleAsync, setIsInDataSource },
    SharedListStore: { getSharedLists, missingSharedListIdsNotLocal },
    SessionStore: { lastURLSaved, setLastURLSaved },
    LoginStore: {
      signInInfos: { userUPN },
    },
  } = useStore();
  const navigate = useNavigate();

  // TAG
  const tag = '10.quickConnect.app/components/domain/Declaration/hooks.useData.ts';

  // On récupère des données depuis l'URL
  const { formId, inboxId, activityId, declarationId, entityInstanceId } = useParams();
  const { pathname, search, state } = useLocation();
  const locationState = state as DeclarationLocationState | null;
  // UO de la déclaration dupliquée ou du brouillon repris, transmise par la liste lors de la navigation
  const declarationOuCodeFromList = locationState?.declarationOuCode;
  // Identifiant de la saisie interrompue à reprendre depuis sa sauvegarde automatique
  const backupDeclarationId = locationState?.backupDeclarationId;
  // Saisie reprise depuis sa sauvegarde automatique : nouvelle saisie ou élément de la boîte de réception (un brouillon ou
  // une déclaration se rouvrent depuis leur propre enregistrement)
  const isResumableEntry = !!formId && !declarationId;
  const isNewEntry = isResumableEntry && !inboxId;

  const [context, setCurrentContext] = useState<DeclarationContext>();
  const [declaration, setDeclaration] = useState<FieldDesc[]>();
  const [declarationData, setDeclarationData] = useState<ItemData>({});
  const [entityData, setEntityData] = useState<EntityData>();

  const [currentActivityId, setCurrentActivityId] = useState<string | undefined>(activityId ?? uuidv4());

  // On appelle les custom hooks
  const { getContext, getContextByDeepLink, getEquipmentByDeepLink, checkEntityDataWithFormContext } =
    useGetDeclarationContext();
  const { hydrateDeclarationWithEquipmentAttributesAsync } = useHydrateDeclarationWithEquipment();
  const { initInternalFieldData } = useHydrateDeclarationWithInternalData();
  const { populateDefaultChoices } = useHydrateDefaultChoices();
  const { hydrateChoiceList } = useHydrateChoiceList();

  const { registerValidState } = useFieldAutoComplete();

  // Permet de rendre la déclaration éditable ou non (avant on se basait sur le isDraft mais cela bloquait dans le cas d'une duplication de déclaration)
  const [isEditable, setIsEditable] = useState<boolean>(false);

  const [formUnauthorized, setFormUnauthorized] = useState<boolean>(false);
  // Permet de savoir s'il l'appel à l'api .../app/getformcontext retourne une erreur et stocke le nom de form
  const [hasFormRetrievalError, setHasFormRetrievalError] = useState<boolean>(false);

  // Permet de stocker le champ dont l'initialisation a échoué
  const [initFieldError, setInitFieldError] = useState<string>('');

  const { lockAsync } = useEditLock();

  // Problème sur la duplication des ids des déclarations - contournement par une Ref
  const declarationIdRef = useRef<string>('');

  // Contient l'ID stable de la déclaration
  // Sert à éviter de lire un dclId obsolète avant re-render
  const stableDclIdRef = useRef<string>('');

  // Fige l'ID de la déclaration pour toute la durée d'init ; lu ensuite depuis initializeFields.
  const applyDeclarationId = useCallback(
    (id: string): void => {
      stableDclIdRef.current = id;
      setDeclarationStoreId(id);
    },
    [setDeclarationStoreId],
  );

  // Une déclaration envoyée se consulte ; un brouillon, une nouvelle saisie, un deeplink ou une duplication se modifient
  const isEditableContext = useCallback(
    ({ isDraft }: DeclarationContext): boolean =>
      !!(isDraft || declarationId === undefined || isDeepLink || isDuplicated),
    [declarationId, isDeepLink, isDuplicated],
  );

  // Saisie interrompue reprise depuis sa sauvegarde automatique (sinon, elle a été terminée entre-temps)
  const isResumedBackup = useCallback(
    ({ id }: DeclarationContext): boolean => !!backupDeclarationId && id === backupDeclarationId,
    [backupDeclarationId],
  );

  // Saisie commencée avant cette ouverture : la quitter demande toujours à l'utilisateur ce qu'il veut en faire
  const isStartedEntry = useCallback(
    (newContext: DeclarationContext): boolean =>
      isResumedBackup(newContext) || (!!newContext.isDraft && !!declarationId && !isDuplicated) || !!inboxId,
    [isResumedBackup, declarationId, isDuplicated, inboxId],
  );

  const initializeFields = useCallback(
    async (newContext: DeclarationContext, entityDataFromDL?: EntityData, isReadOnly = false): Promise<FieldDesc[]> => {
      const {
        formBody,
        formType,
        itemData,
        editedData,
        entitySchemaId,
        templateBodies,
        injectedData,
        internalData,
        name: formName,
        tokenSAS,
      } = newContext;

      // On set les valeurs dans le logger concernant le DeclarationContext
      CustomLogger.getInstance().setDeclarationInfos(formType, formName, userUPN);

      const dclIsEditable = !isReadOnly && isEditableContext(newContext);
      setIsEditable((): boolean => dclIsEditable);

      openEntry({
        isEditable: dclIsEditable,
        // Les modifications d'une saisie interrompue ne sont pas encore enregistrées
        isModified: dclIsEditable && isResumedBackup(newContext),
        isStarted: dclIsEditable && isStartedEntry(newContext),
      });

      setUrlToUseForImagesBank(tokenSAS);

      // On parse les champs du formulaire
      const parsedBody: FieldDesc[] = formBody ? JSONBig.parse(formBody) : [];
      let initializedFields: FieldDesc[] = [];

      let lastFieldFullPathId = '';

      try {
        const mapped = parsedBody.map((field) => {
          // on mémorise l’id avant de mapper
          lastFieldFullPathId = field?.id;
          return mapFieldDesc(field, undefined, templateBodies, newContext);
        });

        initializedFields = mapDesactivatedAutomation(mapped, !dclIsEditable);
      } catch (e) {
        setInitFieldError(lastFieldFullPathId);
      }

      const newFlattenFields = flatten(initializedFields ?? [], (i) => {
        if (i.fieldType === FieldType.RepeatableGroup) {
          return (i as RepeatableGroupDesc).groupTemplate;
        }
        return i.items;
      });

      // On récupère les sharedLists - On pense aussi à vérifier les groupes répétables et les groupTemplate
      const sharedListIds = getSharedListIds(newFlattenFields);

      if (sharedListIds.length > 0) {
        await getSharedLists(sharedListIds);
      }

      await hydrateChoiceList(initializedFields);

      populateDefaultChoices(initializedFields);

      // si on est dans le cas d'une duplication de déclaration
      if (isDuplicated) {
        newContext.id = undefined;
      }

      // Dans le cas des DeepLink
      if (isDeepLink) {
        // Mise a jour des données
        const autoCompleteInternal = { registerValidState, setIsInDataSource };
        await setDeepLinkValues(initializedFields, injectedData, autoCompleteInternal);

        // Recherche des entityData si besoin
        const entityInfos: Record<string, { instanceId: string; schemaId: string }> | undefined =
          await getEntityDataByDeepLink(newFlattenFields, synchronizeEquipementsMultipleAsync);

        if (entityInfos) {
          for (const key in entityInfos) {
            const { instanceId, schemaId } = entityInfos[key];
            const entityDataToSet = await getEntityData(schemaId, instanceId);
            const field: FieldDesc = initializedFields.find(({ fullPathId }: FieldDesc) => fullPathId === key)!;

            initializedFields.forEach((item: FieldDesc) => {
              updatePropertyFieldOrChild(key, field, 'entityData', entityDataToSet);
            });
          }
        }
      }

      if (editedData && formType !== FormType.Workflow) {
        // si on a une déclaration on restaure les champs tels qu'ils l'étaient
        await setHistoricValues(
          editedData,
          templateBodies,
          newFlattenFields,
          stableDclIdRef.current, // Utilise l'ID stable pour éviter un dclId vide capturé avant re-render
          newContext.id ? false : true,
          false,
          formType,
        );

        // Initialisation des internalData
        if (internalData) {
          await initInternalFieldData(newFlattenFields, internalData, dclIsEditable, !!isDuplicated);
        }
      }
      // Si il n'y a pas de données de déclaration
      else {
        if (itemData) {
          // On retourne à l'étape précédent du workflow, pour simplifier le workflow, on part du principe qu'on doit avoir le bon currentStep...
          const { Workflow, PrefilledData } = itemData;
          if (Workflow) {
            const { currentWorkflowState } = Workflow;
            const index = parsedBody.findIndex((f) => f.id === Workflow.currentStep);

            // En édition, on applique les transitions de workflow
            if (dclIsEditable) {
              switch (currentWorkflowState) {
                case WorkflowStatus.ToReview:
                  Workflow.currentStep = parsedBody[index - 1].id;
                  Workflow.currentWorkflowState = WorkflowStatus.None;
                  break;
                case WorkflowStatus.ToForward:
                  Workflow.currentStep = parsedBody[index + 1].id;
                  Workflow.currentWorkflowState = WorkflowStatus.None;
                  break;
              }
            }
            // En consultation, on garde le currentStep tel quel (l'étape validée)

            // On réadapte le desactivatedAutomation
            if (dclIsEditable) desactivatedAutomationWkf(initializedFields, Workflow.currentStep);

            // Verifions si des données sont déjà enregistrées:
            if (editedData) {
              await setHistoricValues(
                editedData,
                templateBodies,
                newFlattenFields,
                stableDclIdRef.current, // Utilise l'ID stable pour éviter un dclId vide capturé avant re-render
                isDuplicated ?? false,
                false,
                formType,
                Workflow.currentStep,
              );

              // Initialisation des internalData
              if (internalData) {
                await initInternalFieldData(newFlattenFields, internalData, dclIsEditable, !!isDuplicated);
              }
            } else if (PrefilledData) {
              await setHistoricValues(
                JSON.stringify(PrefilledData),
                templateBodies,
                newFlattenFields,
                stableDclIdRef.current, // Utilise l'ID stable pour éviter un dclId vide capturé avant re-render
                isDuplicated ?? false,
                true,
                formType,
                Workflow.currentStep,
              );
            }
          } else if (PrefilledData) {
            // Gestion des inboxes (Attention, on ne prend pas en compte les PrefilledData dans le cas d'une reprise de saisie WorkFlow après une mise en pause)
            if (editedData) {
              await setHistoricValues(
                editedData,
                templateBodies,
                newFlattenFields,
                stableDclIdRef.current, // Utilise l'ID stable pour éviter un dclId vide capturé avant re-render
                !!newContext.id,
                false,
                formType,
              );
            } else {
              await setHistoricValues(
                JSON.stringify(PrefilledData),
                templateBodies,
                newFlattenFields,
                stableDclIdRef.current, // Utilise l'ID stable pour éviter un dclId vide capturé avant re-render
                false,
                true,
                formType,
              );

              // Initialisation des internalData
              if (internalData) {
                await initInternalFieldData(newFlattenFields, internalData, dclIsEditable, !!isDuplicated);
              }
            }
          }
          if (formType === FormType.Workflow && itemData.Workflow === undefined) {
            const newItemData: ItemData = {
              ...itemData,
              Workflow: {
                currentStep: parsedBody[0].id,
                currentWorkflowState: WorkflowStatus.None,
                historicalStepDataList: [],
              } as WorkflowData,
            };

            // desactivatedAutomation
            desactivatedAutomationWkf(initializedFields, parsedBody[0].id);

            setDeclarationData(newItemData);
          } else {
            setDeclarationData(itemData);
          }
        }
        // Si on est sur un workflow, on initialise ItemData
        else if (formType === FormType.Workflow) {
          const newItemData: ItemData = {
            Workflow: {
              currentStep: parsedBody[0].id,
              currentWorkflowState: WorkflowStatus.None,
              historicalStepDataList: [],
            } as WorkflowData,
          };
          setDeclarationData(newItemData);

          // Verifions si des données sont déjà enregistrées:
          if (editedData) {
            await setHistoricValues(
              editedData,
              templateBodies,
              newFlattenFields,
              stableDclIdRef.current, // Utilise l'ID stable pour éviter un dclId vide capturé avant re-render
              newContext.id ? true : false,
              false,
              formType,
            );
          }

          // Initialisation des internalData
          if (internalData) {
            await initInternalFieldData(newFlattenFields, internalData, dclIsEditable, !!isDuplicated);
          }
        }
        // Si on est pas sur une déclaration existante, on set les valeur des binding
        if (entitySchemaId && (entityInstanceId || entityDataFromDL)) {
          const getEntity = async (): Promise<FieldDesc[]> => {
            let newEntityData: EntityData | undefined;
            if (!entityDataFromDL && entityInstanceId) {
              newEntityData = await getEntityData(entitySchemaId, entityInstanceId);
            } else {
              newEntityData = entityDataFromDL;
            }

            if (newEntityData) {
              setEntityData(newEntityData);

              /* eslint-disable-next-line  @typescript-eslint/no-non-null-assertion */
              const entityFieldsInitialized = hydrateDeclarationWithEquipmentAttributesAsync(
                initializedFields,
                newEntityData!,
              );

              return entityFieldsInitialized;
            }
            return initializedFields;
          };
          const fieldsEntityInitialized = await getEntity();

          return fieldsEntityInitialized;
        }
      }
      return initializedFields;
    },
    [
      userUPN,
      isEditableContext,
      isResumedBackup,
      isStartedEntry,
      isDeepLink,
      isDuplicated,
      openEntry,
      setUrlToUseForImagesBank,
      hydrateChoiceList,
      populateDefaultChoices,
      getSharedLists,
      registerValidState,
      setIsInDataSource,
      synchronizeEquipementsMultipleAsync,
      getEntityData,
      initInternalFieldData,
      entityInstanceId,
      hydrateDeclarationWithEquipmentAttributesAsync,
    ],
  );

  const flattenFields = useMemo(() => flatten(declaration ?? [], (i) => i.items), [declaration]);

  const flattenFieldsScoped = useMemo(() => {
    return flatten(declaration ?? [], (f: FieldDesc) => {
      const children = f.items ?? [];

      if (f.fieldType === FieldType.RepeatableGroup) {
        const rg = f as RepeatableGroupDesc;
        if (!currentBaseScope) {
          // Scope parent : retourner tous les items
          return rg.items ?? [];
        }
        const scopedRow = rg.items?.find((it) => it.fullPathId === currentBaseScope);
        return scopedRow ? [scopedRow] : [];
      }

      return children;
    });
  }, [currentBaseScope, declaration]);

  const { t } = useTranslation();

  const { checkThenRun, promptProps } = useOuSwitchPrompt();

  // Décision du contrôle d'UO selon la façon dont la déclaration est ouverte
  const getOuDecision = useCallback(
    async (target: OuDeclarationTarget): Promise<OuDecision> => {
      // Duplication (règle du formulaire si l'UO n'est pas connue) ou reprise d'un brouillon : dans l'UO de saisie
      if (declarationId && (isDuplicated || target.isDraft)) {
        const declarationOuCode = declarationOuCodeFromList || (await getDeclarationOuCode(declarationId));
        return decisionForDeclaration(declarationOuCode, isDuplicated ? target : undefined);
      }
      // Inbox : dans son UO
      if (inboxId) return decisionForInbox(getInboxOuCode(inboxId));
      // Nouveau formulaire ou deeplink
      return decisionForForm(target);
    },
    [
      isDuplicated,
      declarationId,
      inboxId,
      declarationOuCodeFromList,
      getDeclarationOuCode,
      getInboxOuCode,
      decisionForDeclaration,
      decisionForInbox,
      decisionForForm,
    ],
  );

  // Ouverture par la liste, une URL ou un nouvel onglet : même contrôle d'UO que dans les listes, qui ont déjà fait la
  // bascule le cas échéant (la décision est alors une ouverture directe)
  const runWithOuCheck = useCallback(
    async (target: OuDeclarationTarget | undefined, proceed: () => Promise<void>): Promise<void> => {
      // Lecture seule d'une déclaration existante : consultable depuis n'importe quelle UO
      if (!target || (!!declarationId && !isDuplicated && !isDeepLink && !target.isDraft)) {
        await proceed();
        return;
      }

      const refuseOpening = () => navigate(FORMS, { replace: true });
      await checkThenRun(() => getOuDecision(target), proceed, {
        context: getOuPromptContext(declarationId, inboxId),
        // Bascule refusée : retour à la liste des formulaires
        onRefused: refuseOpening,
        onUnauthorized: isDeepLink
          ? () => {
              toast.error(t('qcapp_form_not_found', { ns: 'forms' }));
              refuseOpening();
            }
          : () => setFormUnauthorized(true),
      });
    },
    [declarationId, inboxId, isDuplicated, isDeepLink, checkThenRun, getOuDecision, navigate, t],
  );

  const showContextAsync = useCallback(
    async (newContext: DeclarationContext, entityDataFromDL?: EntityData, isReadOnly = false): Promise<void> => {
      const newFields = await initializeFields(newContext, entityDataFromDL, isReadOnly);
      setDeclaration(newFields);
      setCurrentContext(newContext);
      if (newContext.activityId) {
        setCurrentActivityId(newContext.activityId);
      }
    },
    [initializeFields],
  );

  const { showPrompt: showOpenedElsewherePrompt, promptProps: openedElsewherePromptProps } = useOpenedElsewherePrompt();

  // Proposition refusée : retour à la liste d'où la saisie est ouverte
  const leaveToList = useCallback((): void => {
    navigate(inboxId ? INBOXES : declarationId ? DECLARATIONS : FORMS, { replace: true });
  }, [inboxId, declarationId, navigate]);

  // Une saisie n'est modifiable que dans un seul onglet : prend ses verrous, ou propose de la consulter en lecture seule
  // si elle est déjà ouverte dans un autre onglet (retourne alors false)
  const lockEditableContextAsync = useCallback(
    async (newContext: DeclarationContext, entityDataFromDL?: EntityData): Promise<boolean> => {
      if (!isEditableContext(newContext)) return true;

      const lockNames = [getDeclarationEditLockName(stableDclIdRef.current)];
      if (inboxId) lockNames.push(getInboxEditLockName(inboxId));

      // Le formulaire est signalé en cours de saisie aux autres onglets
      if (await lockAsync(lockNames, [getFormEditLockName(newContext.formId)])) return true;

      showOpenedElsewherePrompt(
        {
          kind: 'entry',
          name: (inboxId && getInboxSubject(inboxId)) || newContext.name,
          isInbox: !!inboxId,
          action: () => showContextAsync(newContext, entityDataFromDL, true),
        },
        leaveToList,
      );
      return false;
    },
    [isEditableContext, inboxId, lockAsync, showOpenedElsewherePrompt, getInboxSubject, showContextAsync, leaveToList],
  );

  const openContextAsync = useCallback(
    async (newContext: DeclarationContext): Promise<void> => {
      if (await lockEditableContextAsync(newContext))
        await runWithOuCheck(newContext, () => showContextAsync(newContext));
    },
    [lockEditableContextAsync, runWithOuCheck, showContextAsync],
  );

  // On récupère le contexte
  useEffectOnce(() => {
    if (lastURLSaved) setLastURLSaved(undefined);
    declarationIdRef.current = uuidv4();

    applyDeclarationId(!isDuplicated && !!declarationId ? declarationId : declarationIdRef.current);

    // Set l'observable pour le deepLink (utile pour contourner certaines règles dans les useEffect des fields).
    setIsDeepLink(!!isDeepLink);

    // Cas du deepLink
    if (isDeepLink) {
      (async () => {
        try {
          // Avec deeplink, on s'assure que l'on a bien une liste des formulaires à jour
          await getFormsList(t);

          const params: URLSearchParams = new URLSearchParams(search);

          if (!params.has('formname'))
            throw new Error(
              '[Client Web] useData file Declaration useEffectOnce function failed: Required formname query parameter when isDeepLink equals true.',
            );

          // Le formulaire doit figurer dans une UO de l'utilisateur, sinon toast et retour à la liste (voir runWithOuCheck)
          await runWithOuCheck({ name: params.get('formname')! }, async () => {
            const newContext = await getContextByDeepLink(params);

            if (params.has('entityInstanceCode')) {
              const entityDataDL = await getEquipmentByDeepLink(params.get('entityInstanceCode')!);

              if (!entityDataDL)
                throw new Error(
                  `[Client Web] - Declaration hook file, useEffectOnce failed: Aucune donnée d'équipement récupérée`,
                );

              if (!newContext) throw new Error(`Pas de context récupéré pour le formName: "${params.get('formname')}"`);

              checkEntityDataWithFormContext(entityDataDL, newContext);

              if (await lockEditableContextAsync(newContext, entityDataDL))
                await showContextAsync(newContext, entityDataDL);
            } else {
              if (newContext && (await lockEditableContextAsync(newContext))) await showContextAsync(newContext);
            }
          });
        } catch (error) {
          errorHandler(tag, error, 'useEffectOnce par deeplink', 'error');
        }
      })();
    }
    // Méthode classique
    if (formId) {
      (async () => {
        // TODO: En mode classique, un utilisateur peut ouvrir un formulaire à partir de son lien sans passer par la liste des formulaires
        // On voudrait s'assurer que le formulaire existe bien dans la liste des formulaires dans ce cas
        // await getFormsList(t);

        // On récupère le formulaire
        const newContext = await getContext(
          isDuplicated,
          formId,
          declarationId,
          inboxId,
          entityInstanceId,
          isResumableEntry ? backupDeclarationId : undefined,
        );

        if (newContext) {
          try {
            if (!isResumableEntry) {
              await openContextAsync(newContext);
              return;
            }

            // Saisie interrompue reprise sous son identifiant ; si elle a été terminée entre-temps, elle repart de zéro
            if (backupDeclarationId && isResumedBackup(newContext)) applyDeclarationId(backupDeclarationId);

            // L'état de la saisie est gardé dans l'historique : un rechargement ou une duplication de l'onglet la reprend
            // depuis sa sauvegarde automatique, sans redemander de confirmation
            const keepEntryInHistory = (entryState?: DeclarationLocationState): void =>
              navigate(`${pathname}${search}`, {
                replace: true,
                state: { ...locationState, backupDeclarationId: stableDclIdRef.current, ...entryState },
              });
            keepEntryInHistory();

            // Formulaire ouvert par un lien alors qu'il est en cours de saisie dans un autre onglet : comme depuis la
            // liste des formulaires, l'utilisateur choisit de faire une nouvelle saisie ou de revenir à la liste
            if (
              isNewEntry &&
              !locationState?.isNewEntryConfirmed &&
              !isResumedBackup(newContext) &&
              (await isFormEditedElsewhereAsync(formId))
            ) {
              showOpenedElsewherePrompt(
                {
                  kind: 'form',
                  name: newContext.name,
                  action: async () => {
                    keepEntryInHistory({ isNewEntryConfirmed: true });
                    await openContextAsync(newContext);
                  },
                },
                leaveToList,
              );
              return;
            }

            await openContextAsync(newContext);
          } catch (error) {
            errorHandler(tag, error, 'useEffectOnce par ouverture de formulaire', 'error');
          }
        } else {
          setHasFormRetrievalError(true);
        }
      })();
    }
  });

  // Le contexte de trace du formulaire (nom + type) ne doit etre purge qu'a la sortie reelle de la
  // declaration, et pas au gre des changements de dependances: un Workflow enchaine ses etapes dans
  // la meme declaration, et initializeFields repositionne le contexte a chaque etape ouverte.
  useEffect(
    () => () => {
      CustomLogger.getInstance().resetDeclarationInfos();
    },
    [],
  );

  const onGoBack = useCallback(
    function () {
      openDialog();
    },
    [openDialog],
  );
  const refIsDeletedDeclarationContext = useRef<boolean>(isDeletedDeclarationContext);
  useEffect(() => {
    if (refIsDeletedDeclarationContext.current) {
      setIsDeletedDeclarationContext(false);
    }
  }, [setIsDeletedDeclarationContext]);

  useEffect(() => {
    return () => {
      setCloseDrawer();
    };
  }, [setCloseDrawer]);

  // La saisie, ouverte par cette page, est fermée en la quittant, quelle que soit la façon de la quitter
  useEffect(() => closeEntry, [closeEntry]);

  useEffect(() => {
    if (mustConfirmDeclarationExit) {
      // L'état de navigation est conservé : il porte l'identifiant de la saisie à reprendre au rechargement
      window.history.pushState(window.history.state, '', document.URL);
      window.addEventListener('popstate', onGoBack);

      return () => {
        resetIsInDataSource();
        window.removeEventListener('popstate', onGoBack);
      };
    }
  }, [resetIsInDataSource, onGoBack, mustConfirmDeclarationExit, openDeclarationDialog]);

  return {
    context,
    entityData,
    declaration,
    flattenFieldsScoped: flattenFieldsScoped,
    flattenFields,
    declarationData,
    setDeclarationData,
    setDeclaration,
    currentActivityId,
    inboxId,
    declarationId: stableDclIdRef.current,
    isEditable,
    isDeletedDeclarationContext,
    formUnauthorized,
    initFieldError,
    missingSharedListIdsNotLocal,
    hasFormRetrievalError,
    promptProps,
    openedElsewherePromptProps,
  };
};

export default useData;
